/**
 * Financial Services API Service
 * Handles API calls for bank accounts, payment processors, and crypto exchanges
 */
import logger from '../utils/logger';
import { API_CONFIG } from '../config/api';
import {
  getE2EFinancialApplicationsFixture,
  getE2EFinancialProvidersFixture,
  getE2EProviderReadinessFixture,
  isE2ECommandFixtureEnabled,
} from '../config/e2eCommandFixture';

const API_BASE_URL = API_CONFIG.BASE_URL;

// Store access token for authenticated requests
let accessToken = null;

/**
 * Set access token for authenticated requests
 * @param {string} token - JWT access token from Auth0
 */
const setAccessToken = (token) => {
  accessToken = token;
};

/**
 * Get authorization headers with JWT token
 * @returns {Object} Headers object with Authorization
 */
const getAuthHeaders = () => {
  const headers = {
    'Content-Type': 'application/json',
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }
  return headers;
};

/**
 * Financial Services API methods
 */
const financialServicesApi = {
  /**
   * Get all available financial service providers
   * @returns {Promise<Object>} Providers organized by type
   */
  async getProviders() {
    try {
      if (isE2ECommandFixtureEnabled()) {
        return {
          success: true,
          data: getE2EFinancialProvidersFixture(),
        };
      }

      const response = await fetch(`${API_BASE_URL}/api/financial/providers`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch providers');
      }

      return data;
    } catch (error) {
      logger.error('Error fetching financial providers:', error);
      throw error;
    }
  },

  /**
   * Get details for a specific provider
   * @param {string} serviceType - bank, payment_processor, or crypto_exchange
   * @param {string} providerId - Provider identifier
   * @returns {Promise<Object>} Provider details
   */
  async getProviderDetails(serviceType, providerId) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/financial/providers/${serviceType}/${providerId}`,
        {
          method: 'GET',
          headers: getAuthHeaders(),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch provider details');
      }

      return data;
    } catch (error) {
      logger.error('Error fetching provider details:', error);
      throw error;
    }
  },

  /**
   * Get all financial applications for a company
   * @param {string} registrationId - Company registration UUID
   * @returns {Promise<Object>} List of applications
   */
  async getCompanyApplications(registrationId) {
    try {
      if (isE2ECommandFixtureEnabled()) {
        return {
          success: true,
          data: getE2EFinancialApplicationsFixture(registrationId),
        };
      }

      const response = await fetch(
        `${API_BASE_URL}/api/financial/companies/${registrationId}/applications`,
        {
          method: 'GET',
          headers: getAuthHeaders(),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch applications');
      }

      return data;
    } catch (error) {
      logger.error('Error fetching company applications:', error);
      throw error;
    }
  },

  /**
   * Check whether a direct provider application can be prefilled from Rely
   * @param {string} registrationId - Company registration UUID
   * @param {string} providerId - Provider identifier
   * @returns {Promise<Object>} Readiness data with missing/completed requirements
   */
  async getProviderReadiness(registrationId, providerId) {
    try {
      if (isE2ECommandFixtureEnabled()) {
        return getE2EProviderReadinessFixture(providerId);
      }

      const response = await fetch(
        `${API_BASE_URL}/api/financial/companies/${registrationId}/providers/${providerId}/readiness`,
        {
          method: 'GET',
          headers: getAuthHeaders(),
        }
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(
          data.message || data.error || 'Failed to check provider readiness'
        );
      }

      return data;
    } catch (error) {
      logger.error('Error checking provider readiness:', error);
      throw error;
    }
  },

  /**
   * Create a new financial service application
   * @param {string} registrationId - Company registration UUID
   * @param {Object} applicationData - Application data
   * @returns {Promise<Object>} Created application
   */
  async createApplication(registrationId, applicationData) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/financial/companies/${registrationId}/applications`,
        {
          method: 'POST',
          headers: getAuthHeaders(),

          body: JSON.stringify(applicationData),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create application');
      }

      return data;
    } catch (error) {
      logger.error('Error creating application:', error);
      throw error;
    }
  },

  /**
   * Launch a direct provider application from the marketplace
   * @param {string} registrationId - Company registration UUID
   * @param {string} providerId - Provider identifier
   * @returns {Promise<Object>} Launch URL and tracking data
   */
  async launchProviderApplication(registrationId, providerId) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/financial/companies/${registrationId}/providers/${providerId}/launch`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
        }
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        const error = new Error(
          data.message || data.error || 'Failed to launch provider application'
        );
        error.data = data;
        error.status = response.status;
        throw error;
      }

      return data;
    } catch (error) {
      logger.error('Error launching provider application:', error);
      throw error;
    }
  },

  /**
   * Complete the guarded provider workflow in an isolated sandbox.
   * The backend rejects this operation outside its sandbox-only allowlist.
   * @param {string} registrationId - Company registration UUID
   * @param {string} providerId - Provider identifier
   * @returns {Promise<Object>} Projected provider application status
   */
  async runProviderSandboxAutomation(registrationId, providerId) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/financial/companies/${registrationId}/providers/${providerId}/sandbox/full-automation`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
        }
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(
          data.message || data.error || 'Failed to complete sandbox automation'
        );
      }

      return data;
    } catch (error) {
      logger.error('Error completing provider sandbox automation:', error);
      throw error;
    }
  },

  /**
   * Create an assisted provider request without payment checkout
   * @param {string} registrationId - Company registration UUID
   * @param {string} providerId - Provider identifier
   * @param {Object} requestData - Assisted request data
   * @returns {Promise<Object>} Created or existing application
   */
  async requestAssistedApplication(registrationId, providerId, requestData = {}) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/financial/companies/${registrationId}/providers/${providerId}/assisted-request`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(requestData),
        }
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(
          data.message || data.error || 'Failed to request provider assistance'
        );
      }

      return data;
    } catch (error) {
      logger.error('Error requesting provider assistance:', error);
      throw error;
    }
  },

  /**
   * Update an existing application
   * @param {string} applicationId - Application ID
   * @param {Object} updateData - Data to update
   * @returns {Promise<Object>} Updated application
   */
  async updateApplication(applicationId, updateData) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/financial/applications/${applicationId}`,
        {
          method: 'PUT',
          headers: getAuthHeaders(),

          body: JSON.stringify(updateData),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update application');
      }

      return data;
    } catch (error) {
      logger.error('Error updating application:', error);
      throw error;
    }
  },

  /**
   * Submit an application for review
   * @param {string} applicationId - Application ID
   * @param {string} userEmail - User's email (optional)
   * @returns {Promise<Object>} Submitted application
   */
  async submitApplication(applicationId, userEmail = null) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/financial/applications/${applicationId}/submit`,
        {
          method: 'POST',
          headers: getAuthHeaders(),

          body: JSON.stringify({ user_email: userEmail }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to submit application');
      }

      return data;
    } catch (error) {
      logger.error('Error submitting application:', error);
      throw error;
    }
  },

  /**
   * Delete a draft application
   * @param {string} applicationId - Application ID
   * @returns {Promise<Object>} Success response
   */
  async deleteApplication(applicationId) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/financial/applications/${applicationId}`,
        {
          method: 'DELETE',
          headers: getAuthHeaders(),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to delete application');
      }

      return data;
    } catch (error) {
      logger.error('Error deleting application:', error);
      throw error;
    }
  },

  /**
   * Get application status history
   * @param {string} applicationId - Application ID
   * @returns {Promise<Object>} Status history
   */
  async getApplicationHistory(applicationId) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/financial/applications/${applicationId}/history`,
        {
          method: 'GET',
          headers: getAuthHeaders(),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch application history');
      }

      return data;
    } catch (error) {
      logger.error('Error fetching application history:', error);
      throw error;
    }
  },

  /**
   * Create a PaymentIntent for financial service application (legacy - deprecated)
   * @deprecated Use createApplicationPaymentIntent instead
   */
  async createPaymentIntent({
    registrationId,
    providerId,
    providerName,
    amountCents,
    currency = 'USD',
  }) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/create-payment-intent`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            amount: amountCents,
            currency: currency.toUpperCase(),
            serviceType: 'financial_service_application',
            companyId: registrationId,
            paymentMethodType: 'card',
            metadata: {
              provider_id: providerId,
              provider_name: providerName,
              application_type: 'financial_service',
              registration_id: registrationId,
            },
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to create payment intent');
      }

      return {
        success: true,
        clientSecret: data.clientSecret,
        paymentIntentId: data.paymentIntent?.id,
        registrationId: data.registrationId,
      };
    } catch (error) {
      logger.error('Error creating payment intent:', error);
      return {
        success: false,
        error: error.message || 'Failed to create payment intent',
      };
    }
  },

  /**
   * Create a draft financial application (without payment)
   * @param {string} registrationId - Company registration UUID
   * @param {Object} applicationData - Application data
   * @returns {Promise<Object>} Created application
   */
  async createDraftApplication(registrationId, applicationData) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/financial/companies/${registrationId}/applications`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(applicationData),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to create application');
      }

      return data;
    } catch (error) {
      logger.error('Error creating draft application:', error);
      throw error;
    }
  },

  /**
   * Create a PaymentIntent for an existing financial application
   * @param {string} applicationId - Application UUID
   * @param {string} tenantId - Tenant UUID for authorization
   * @returns {Promise<Object>} { clientSecret, paymentIntentId, applicationId }
   */
  async createApplicationPaymentIntent(applicationId, tenantId) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/financial/applications/${applicationId}/create-payment-intent`,
        {
          method: 'POST',
          headers: {
            ...getAuthHeaders(),
            'X-Tenant-Id': tenantId,
          },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to create payment intent');
      }

      return {
        success: data.success,
        clientSecret: data.client_secret,
        paymentIntentId: data.payment_intent_id,
        applicationId: data.application_id,
        amount: data.amount,
        currency: data.currency,
        error: data.message,
      };
    } catch (error) {
      logger.error('Error creating application payment intent:', error);
      return {
        success: false,
        error: error.message || 'Failed to create payment intent',
      };
    }
  },

  /**
   * Confirm and process payment for an application (polling for dev environments)
   * @param {string} applicationId - Application UUID
   * @returns {Promise<Object>} Payment confirmation result
   */
  async confirmApplicationPayment(applicationId) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/financial/applications/${applicationId}/confirm-payment`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
        }
      );

      const data = await response.json();

      return {
        success: data.success,
        applicationId: data.application_id,
        message: data.message,
      };
    } catch (error) {
      logger.error('Error confirming application payment:', error);
      return {
        success: false,
        error: error.message || 'Failed to confirm payment',
      };
    }
  },

  /**
   * Get payment status for an application
   * @param {string} applicationId - Application UUID
   * @returns {Promise<Object>} Payment status
   */
  async getApplicationPaymentStatus(applicationId) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/financial/applications/${applicationId}/payment-status`,
        {
          method: 'GET',
          headers: getAuthHeaders(),
        }
      );

      const data = await response.json();

      return {
        success: data.success,
        applicationId: data.application_id,
        message: data.message,
      };
    } catch (error) {
      logger.error('Error getting payment status:', error);
      return {
        success: false,
        error: error.message || 'Failed to get payment status',
      };
    }
  },

  // =========================================================================
  // CREDENTIAL VIEWING ENDPOINTS (Public - token-based auth)
  // =========================================================================

  /**
   * Verify 2FA code for credential access
   * @param {string} accessToken - Secure access token from email link
   * @param {string} verificationCode - 6-digit 2FA code
   * @returns {Promise<Object>} Verification result
   */
  async verifyCredentialCode(accessToken, verificationCode) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/financial/credentials/verify`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            access_token: accessToken,
            verification_code: verificationCode,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.message || data.error || 'Verification failed',
          remainingAttempts: data.remaining_attempts,
          locked: data.locked,
        };
      }

      return {
        success: true,
        message: data.message,
      };
    } catch (error) {
      logger.error('Error verifying credential code:', error);
      return {
        success: false,
        error: error.message || 'Failed to verify code',
      };
    }
  },

  /**
   * View credentials (one-time access after 2FA verification)
   * @param {string} accessToken - Secure access token from email link
   * @returns {Promise<Object>} Decrypted credentials
   */
  async viewCredentials(accessToken) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/financial/credentials/view`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            access_token: accessToken,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.message || data.error || 'Failed to view credentials',
        };
      }

      return {
        success: true,
        username: data.username,
        password: data.password,
        notes: data.notes,
        providerName: data.provider_name,
      };
    } catch (error) {
      logger.error('Error viewing credentials:', error);
      return {
        success: false,
        error: error.message || 'Failed to view credentials',
      };
    }
  },

  /**
   * Acknowledge that user has saved credentials
   * @param {string} accessToken - Secure access token from email link
   * @returns {Promise<Object>} Acknowledgment result
   */
  async acknowledgeCredentials(accessToken) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/financial/credentials/acknowledge`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            access_token: accessToken,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.message || data.error || 'Failed to acknowledge',
        };
      }

      return {
        success: true,
        message: data.message,
      };
    } catch (error) {
      logger.error('Error acknowledging credentials:', error);
      return {
        success: false,
        error: error.message || 'Failed to acknowledge credentials',
      };
    }
  },
};

// Export setAccessToken as a named export
export { setAccessToken };

export default financialServicesApi;
